import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  DashboardHistoryData,
  ChartConfig,
} from '../../models/dashboard-history.model';
import {
  processDashboardData,
  createSpeedChartConfig,
  createAltitudeChartConfig,
  createSpeedDistributionChartConfig,
} from '../../helpers/dashboard-data.helper';
import { StoreHistory } from '../../../../models/manager-content-vehicle-history-drawer.model';
import { AlertService } from 'src/app/alertas/services/alert.service';

// Declaración para Chart.js
declare var Chart: any;

@Component({
  selector: 'app-dashboard-history',
  templateUrl: './dashboard-history.component.html',
  styleUrls: ['./dashboard-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardHistoryComponent implements OnDestroy, OnChanges {
  @Input() storeHistory: StoreHistory | null = null;
  @Input() visible: boolean = false;

  @ViewChild('speedChart') speedChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('altitudeChart') altitudeChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('speedDistributionChart')
  speedDistributionChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureBle1ChartRef')
  temperatureBle1ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureBle2ChartRef')
  temperatureBle2ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureBle3ChartRef')
  temperatureBle3ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureBle4ChartRef')
  temperatureBle4ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureEye1ChartRef')
  temperatureEye1ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureEye2ChartRef')
  temperatureEye2ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureEye3ChartRef')
  temperatureEye3ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('temperatureEye4ChartRef')
  temperatureEye4ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityBle1ChartRef')
  humidityBle1ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityBle2ChartRef')
  humidityBle2ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityBle3ChartRef')
  humidityBle3ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityBle4ChartRef')
  humidityBle4ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityEye1ChartRef')
  humidityEye1ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityEye2ChartRef')
  humidityEye2ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityEye3ChartRef')
  humidityEye3ChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('humidityEye4ChartRef')
  humidityEye4ChartRef!: ElementRef<HTMLCanvasElement>;

  dashboardData: DashboardHistoryData | null = null;
  speedChart: any;
  altitudeChart: any;
  speedDistributionChart: any;
  temperatureBle1Chart: any;
  temperatureBle2Chart: any;
  temperatureBle3Chart: any;
  temperatureBle4Chart: any;
  temperatureEye1Chart: any;
  temperatureEye2Chart: any;
  temperatureEye3Chart: any;
  temperatureEye4Chart: any;
  humidityBle1Chart: any;
  humidityBle2Chart: any;
  humidityBle3Chart: any;
  humidityBle4Chart: any;
  humidityEye1Chart: any;
  humidityEye2Chart: any;
  humidityEye3Chart: any;
  humidityEye4Chart: any;

  private destroy$ = new Subject<void>();

  // Configuraciones de visualización
  showSpeedChart = true;
  showAltitudeChart = true;
  showStatistics = true;
  showSpeedDistribution = true;
  showTemperatureBleCharts = true;
  showTemperatureEyeCharts = true;
  showHumidityBleCharts = true;
  showHumidityEyeCharts = true;

  // Datos de temperatura individual
  temperatureBle1Data: { timestamp: Date; temperature: number }[] = [];
  temperatureBle2Data: { timestamp: Date; temperature: number }[] = [];
  temperatureBle3Data: { timestamp: Date; temperature: number }[] = [];
  temperatureBle4Data: { timestamp: Date; temperature: number }[] = [];
  temperatureEye1Data: { timestamp: Date; temperature: number }[] = [];
  temperatureEye2Data: { timestamp: Date; temperature: number }[] = [];
  temperatureEye3Data: { timestamp: Date; temperature: number }[] = [];
  temperatureEye4Data: { timestamp: Date; temperature: number }[] = [];

  // Datos de humedad individual
  humidityBle1Data: { timestamp: Date; humidity: number }[] = [];
  humidityBle2Data: { timestamp: Date; humidity: number }[] = [];
  humidityBle3Data: { timestamp: Date; humidity: number }[] = [];
  humidityBle4Data: { timestamp: Date; humidity: number }[] = [];
  humidityEye1Data: { timestamp: Date; humidity: number }[] = [];
  humidityEye2Data: { timestamp: Date; humidity: number }[] = [];
  humidityEye3Data: { timestamp: Date; humidity: number }[] = [];
  humidityEye4Data: { timestamp: Date; humidity: number }[] = [];

  // Configuraciones de límites desde alertas
  temperatureConfigurations: any[] = [];
  humidityConfigurations: any[] = [];

  // Configuraciones seleccionadas para cada gráfico
  selectedTempConfigBle1: any = null;
  selectedTempConfigBle2: any = null;
  selectedTempConfigBle3: any = null;
  selectedTempConfigBle4: any = null;
  selectedTempConfigEye1: any = null;
  selectedTempConfigEye2: any = null;
  selectedTempConfigEye3: any = null;
  selectedTempConfigEye4: any = null;

  selectedHumConfigBle1: any = null;
  selectedHumConfigBle2: any = null;
  selectedHumConfigBle3: any = null;
  selectedHumConfigBle4: any = null;
  selectedHumConfigEye1: any = null;
  selectedHumConfigEye2: any = null;
  selectedHumConfigEye3: any = null;
  selectedHumConfigEye4: any = null;

  // Valores por defecto
  private readonly DEFAULT_TEMP_LIMITS = { min: -10, max: 50 };
  private readonly DEFAULT_HUMIDITY_LIMITS = { min: 30, max: 80 };

  constructor(
    private cdr: ChangeDetectorRef,
    private alertService: AlertService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeHistory']) {
      this.processData();
    }

    if (changes['visible']) {
      if (changes['visible'].currentValue && this.dashboardData) {
        setTimeout(() => {
          this.initializeCharts();
        }, 200);
      } else if (!changes['visible'].currentValue) {
        this.destroyCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroyCharts();
  }

  private processData(): void {
    if (this.storeHistory) {
      console.log('(DASHBOARD) storeHistory', this.storeHistory);

      // Cargar configuraciones de alertas
      this.temperatureConfigurations =
        this.alertService.getTemperatureConfigurationFromAlerts(
          this.storeHistory.unit.GNSSIMEI
        );
      this.humidityConfigurations =
        this.alertService.getHumedadConfigurationFromAlerts(
          this.storeHistory.unit.GNSSIMEI
        );

      console.log(
        'Temperature configurations:',
        this.temperatureConfigurations
      );
      console.log('Humidity configurations:', this.humidityConfigurations);

      // Establecer configuraciones por defecto (primera de la lista o valores por defecto)
      this.setDefaultConfigurations();

      this.dashboardData = processDashboardData(this.storeHistory);

      // Extraer datos de temperatura para cada sensor
      this.extractTemperatureBleData();
      this.extractTemperatureEyeData();

      // Extraer datos de humedad para cada sensor
      this.extractHumidityBleData();
      this.extractHumidityEyeData();

      this.cdr.detectChanges();
    }
  }

  private setDefaultConfigurations(): void {
    // Establecer configuración por defecto para temperatura
    const defaultTempConfig =
      this.temperatureConfigurations.length > 0
        ? this.temperatureConfigurations[0]
        : {
            nombre: 'Por defecto',
            temperature_min: this.DEFAULT_TEMP_LIMITS.min,
            temperature_max: this.DEFAULT_TEMP_LIMITS.max,
          };

    // Establecer configuración por defecto para humedad
    const defaultHumConfig =
      this.humidityConfigurations.length > 0
        ? this.humidityConfigurations[0]
        : {
            nombre: 'Por defecto',
            humidity_min: this.DEFAULT_HUMIDITY_LIMITS.min,
            humidity_max: this.DEFAULT_HUMIDITY_LIMITS.max,
          };

    // Asignar a todos los gráficos de temperatura
    this.selectedTempConfigBle1 = { ...defaultTempConfig };
    this.selectedTempConfigBle2 = { ...defaultTempConfig };
    this.selectedTempConfigBle3 = { ...defaultTempConfig };
    this.selectedTempConfigBle4 = { ...defaultTempConfig };
    this.selectedTempConfigEye1 = { ...defaultTempConfig };
    this.selectedTempConfigEye2 = { ...defaultTempConfig };
    this.selectedTempConfigEye3 = { ...defaultTempConfig };
    this.selectedTempConfigEye4 = { ...defaultTempConfig };

    // Asignar a todos los gráficos de humedad
    this.selectedHumConfigBle1 = { ...defaultHumConfig };
    this.selectedHumConfigBle2 = { ...defaultHumConfig };
    this.selectedHumConfigBle3 = { ...defaultHumConfig };
    this.selectedHumConfigBle4 = { ...defaultHumConfig };
    this.selectedHumConfigEye1 = { ...defaultHumConfig };
    this.selectedHumConfigEye2 = { ...defaultHumConfig };
    this.selectedHumConfigEye3 = { ...defaultHumConfig };
    this.selectedHumConfigEye4 = { ...defaultHumConfig };
  }

  // Métodos para cambiar configuraciones de límites
  onTemperatureConfigChange(chartType: string, configIndex: number): void {
    const selectedConfig = this.temperatureConfigurations[configIndex] || {
      nombre: 'Por defecto',
      temperature_min: this.DEFAULT_TEMP_LIMITS.min,
      temperature_max: this.DEFAULT_TEMP_LIMITS.max,
    };

    switch (chartType) {
      case 'ble1':
        this.selectedTempConfigBle1 = { ...selectedConfig };
        break;
      case 'ble2':
        this.selectedTempConfigBle2 = { ...selectedConfig };
        break;
      case 'ble3':
        this.selectedTempConfigBle3 = { ...selectedConfig };
        break;
      case 'ble4':
        this.selectedTempConfigBle4 = { ...selectedConfig };
        break;
      case 'eye1':
        this.selectedTempConfigEye1 = { ...selectedConfig };
        break;
      case 'eye2':
        this.selectedTempConfigEye2 = { ...selectedConfig };
        break;
      case 'eye3':
        this.selectedTempConfigEye3 = { ...selectedConfig };
        break;
      case 'eye4':
        this.selectedTempConfigEye4 = { ...selectedConfig };
        break;
    }

    // Reinicializar gráficos con nueva configuración
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  onHumidityConfigChange(chartType: string, configIndex: number): void {
    const selectedConfig = this.humidityConfigurations[configIndex] || {
      nombre: 'Por defecto',
      humidity_min: this.DEFAULT_HUMIDITY_LIMITS.min,
      humidity_max: this.DEFAULT_HUMIDITY_LIMITS.max,
    };

    switch (chartType) {
      case 'ble1':
        this.selectedHumConfigBle1 = { ...selectedConfig };
        break;
      case 'ble2':
        this.selectedHumConfigBle2 = { ...selectedConfig };
        break;
      case 'ble3':
        this.selectedHumConfigBle3 = { ...selectedConfig };
        break;
      case 'ble4':
        this.selectedHumConfigBle4 = { ...selectedConfig };
        break;
      case 'eye1':
        this.selectedHumConfigEye1 = { ...selectedConfig };
        break;
      case 'eye2':
        this.selectedHumConfigEye2 = { ...selectedConfig };
        break;
      case 'eye3':
        this.selectedHumConfigEye3 = { ...selectedConfig };
        break;
      case 'eye4':
        this.selectedHumConfigEye4 = { ...selectedConfig };
        break;
    }

    // Reinicializar gráficos con nueva configuración
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  // Método para obtener la configuración actual de un gráfico
  getCurrentTempConfigIndex(chartType: string): number {
    let currentConfig: any;

    switch (chartType) {
      case 'ble1':
        currentConfig = this.selectedTempConfigBle1;
        break;
      case 'ble2':
        currentConfig = this.selectedTempConfigBle2;
        break;
      case 'ble3':
        currentConfig = this.selectedTempConfigBle3;
        break;
      case 'ble4':
        currentConfig = this.selectedTempConfigBle4;
        break;
      case 'eye1':
        currentConfig = this.selectedTempConfigEye1;
        break;
      case 'eye2':
        currentConfig = this.selectedTempConfigEye2;
        break;
      case 'eye3':
        currentConfig = this.selectedTempConfigEye3;
        break;
      case 'eye4':
        currentConfig = this.selectedTempConfigEye4;
        break;
      default:
        return 0;
    }

    return (
      this.temperatureConfigurations.findIndex(
        (config) => config.nombre === currentConfig?.nombre
      ) || 0
    );
  }

  getCurrentHumConfigIndex(chartType: string): number {
    let currentConfig: any;

    switch (chartType) {
      case 'ble1':
        currentConfig = this.selectedHumConfigBle1;
        break;
      case 'ble2':
        currentConfig = this.selectedHumConfigBle2;
        break;
      case 'ble3':
        currentConfig = this.selectedHumConfigBle3;
        break;
      case 'ble4':
        currentConfig = this.selectedHumConfigBle4;
        break;
      case 'eye1':
        currentConfig = this.selectedHumConfigEye1;
        break;
      case 'eye2':
        currentConfig = this.selectedHumConfigEye2;
        break;
      case 'eye3':
        currentConfig = this.selectedHumConfigEye3;
        break;
      case 'eye4':
        currentConfig = this.selectedHumConfigEye4;
        break;
      default:
        return 0;
    }

    return (
      this.humidityConfigurations.findIndex(
        (config) => config.nombre === currentConfig?.nombre
      ) || 0
    );
  }

  private initializeCharts(): void {
    this.destroyCharts();

    if (!this.dashboardData) {
      console.log('No dashboard data available');
      return;
    }

    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not loaded');
      return;
    }

    console.log('Initializing charts with data:', this.dashboardData);

    // Gráfico de velocidad
    if (
      this.showSpeedChart &&
      this.speedChartRef &&
      this.speedChartRef.nativeElement
    ) {
      try {
        const speedConfig = createSpeedChartConfig(
          this.dashboardData.timeSeriesData
        );
        console.log('Speed chart config:', speedConfig);
        this.speedChart = new Chart(
          this.speedChartRef.nativeElement,
          speedConfig
        );
        console.log('Speed chart created successfully');
      } catch (error) {
        console.error('Error creating speed chart:', error);
      }
    }

    // Gráfico de altitud
    if (
      this.showAltitudeChart &&
      this.altitudeChartRef &&
      this.altitudeChartRef.nativeElement
    ) {
      try {
        const altitudeConfig = createAltitudeChartConfig(
          this.dashboardData.altitudeProfile
        );
        console.log('Altitude chart config:', altitudeConfig);
        this.altitudeChart = new Chart(
          this.altitudeChartRef.nativeElement,
          altitudeConfig
        );
        console.log('Altitude chart created successfully');
      } catch (error) {
        console.error('Error creating altitude chart:', error);
      }
    }

    // Gráfico de distribución de velocidades
    if (
      this.showSpeedDistribution &&
      this.speedDistributionChartRef &&
      this.speedDistributionChartRef.nativeElement
    ) {
      try {
        const distributionConfig = createSpeedDistributionChartConfig(
          this.dashboardData.speedDistribution
        );
        console.log('Distribution chart config:', distributionConfig);
        this.speedDistributionChart = new Chart(
          this.speedDistributionChartRef.nativeElement,
          distributionConfig
        );
        console.log('Distribution chart created successfully');
      } catch (error) {
        console.error('Error creating distribution chart:', error);
      }
    }

    // Gráficos de temperatura BLE individuales
    if (this.showTemperatureBleCharts) {
      this.createTemperatureBleCharts();
    }

    // Gráficos de temperatura EYE individuales
    if (this.showTemperatureEyeCharts) {
      this.createTemperatureEyeCharts();
    }

    // Gráficos de humedad BLE individuales
    if (this.showHumidityBleCharts) {
      this.createHumidityBleCharts();
    }

    // Gráficos de humedad EYE individuales
    if (this.showHumidityEyeCharts) {
      this.createHumidityEyeCharts();
    }
  }

  private destroyCharts(): void {
    if (this.speedChart) {
      this.speedChart.destroy();
      this.speedChart = null;
    }
    if (this.altitudeChart) {
      this.altitudeChart.destroy();
      this.altitudeChart = null;
    }
    if (this.speedDistributionChart) {
      this.speedDistributionChart.destroy();
      this.speedDistributionChart = null;
    }
    if (this.temperatureBle1Chart) {
      this.temperatureBle1Chart.destroy();
      this.temperatureBle1Chart = null;
    }
    if (this.temperatureBle2Chart) {
      this.temperatureBle2Chart.destroy();
      this.temperatureBle2Chart = null;
    }
    if (this.temperatureBle3Chart) {
      this.temperatureBle3Chart.destroy();
      this.temperatureBle3Chart = null;
    }
    if (this.temperatureBle4Chart) {
      this.temperatureBle4Chart.destroy();
      this.temperatureBle4Chart = null;
    }
    if (this.temperatureEye1Chart) {
      this.temperatureEye1Chart.destroy();
      this.temperatureEye1Chart = null;
    }
    if (this.temperatureEye2Chart) {
      this.temperatureEye2Chart.destroy();
      this.temperatureEye2Chart = null;
    }
    if (this.temperatureEye3Chart) {
      this.temperatureEye3Chart.destroy();
      this.temperatureEye3Chart = null;
    }
    if (this.temperatureEye4Chart) {
      this.temperatureEye4Chart.destroy();
      this.temperatureEye4Chart = null;
    }
    if (this.humidityBle1Chart) {
      this.humidityBle1Chart.destroy();
      this.humidityBle1Chart = null;
    }
    if (this.humidityBle2Chart) {
      this.humidityBle2Chart.destroy();
      this.humidityBle2Chart = null;
    }
    if (this.humidityBle3Chart) {
      this.humidityBle3Chart.destroy();
      this.humidityBle3Chart = null;
    }
    if (this.humidityBle4Chart) {
      this.humidityBle4Chart.destroy();
      this.humidityBle4Chart = null;
    }
    if (this.humidityEye1Chart) {
      this.humidityEye1Chart.destroy();
      this.humidityEye1Chart = null;
    }
    if (this.humidityEye2Chart) {
      this.humidityEye2Chart.destroy();
      this.humidityEye2Chart = null;
    }
    if (this.humidityEye3Chart) {
      this.humidityEye3Chart.destroy();
      this.humidityEye3Chart = null;
    }
    if (this.humidityEye4Chart) {
      this.humidityEye4Chart.destroy();
      this.humidityEye4Chart = null;
    }
  }

  toggleSpeedChart(): void {
    this.showSpeedChart = !this.showSpeedChart;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleAltitudeChart(): void {
    this.showAltitudeChart = !this.showAltitudeChart;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleSpeedDistribution(): void {
    this.showSpeedDistribution = !this.showSpeedDistribution;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleTemperatureBleChart(): void {
    this.showTemperatureBleCharts = !this.showTemperatureBleCharts;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleTemperatureEyeChart(): void {
    this.showTemperatureEyeCharts = !this.showTemperatureEyeCharts;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleHumidityBleChart(): void {
    this.showHumidityBleCharts = !this.showHumidityBleCharts;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleHumidityEyeChart(): void {
    this.showHumidityEyeCharts = !this.showHumidityEyeCharts;
    if (this.visible) {
      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    }
  }

  toggleStatistics(): void {
    this.showStatistics = !this.showStatistics;
  }

  private createTemperatureBleCharts(): void {
    const charts = [
      {
        data: this.temperatureBle1Data,
        chart: 'temperatureBle1Chart',
        ref: 'temperatureBle1ChartRef',
        label: 'BLE #1',
        color: '#00acc1',
        config: this.selectedTempConfigBle1,
      },
      {
        data: this.temperatureBle2Data,
        chart: 'temperatureBle2Chart',
        ref: 'temperatureBle2ChartRef',
        label: 'BLE #2',
        color: '#26a69a',
        config: this.selectedTempConfigBle2,
      },
      {
        data: this.temperatureBle3Data,
        chart: 'temperatureBle3Chart',
        ref: 'temperatureBle3ChartRef',
        label: 'BLE #3',
        color: '#42a5f5',
        config: this.selectedTempConfigBle3,
      },
      {
        data: this.temperatureBle4Data,
        chart: 'temperatureBle4Chart',
        ref: 'temperatureBle4ChartRef',
        label: 'BLE #4',
        color: '#5c6bc0',
        config: this.selectedTempConfigBle4,
      },
    ];

    charts.forEach((chartInfo) => {
      if (
        chartInfo.data.length > 0 &&
        (this as any)[chartInfo.ref] &&
        (this as any)[chartInfo.ref].nativeElement
      ) {
        try {
          const minLimit =
            chartInfo.config?.temperature_min ?? this.DEFAULT_TEMP_LIMITS.min;
          const maxLimit =
            chartInfo.config?.temperature_max ?? this.DEFAULT_TEMP_LIMITS.max;

          const config = this.createTemperatureChartConfig(
            chartInfo.data,
            chartInfo.label,
            chartInfo.color,
            minLimit,
            maxLimit
          );
          (this as any)[chartInfo.chart] = new Chart(
            (this as any)[chartInfo.ref].nativeElement,
            config
          );
          console.log(
            `${chartInfo.label} chart created successfully with limits: ${minLimit}°C - ${maxLimit}°C`
          );
        } catch (error) {
          console.error(`Error creating ${chartInfo.label} chart:`, error);
        }
      }
    });
  }

  private createTemperatureEyeCharts(): void {
    const charts = [
      {
        data: this.temperatureEye1Data,
        chart: 'temperatureEye1Chart',
        ref: 'temperatureEye1ChartRef',
        label: 'EYE #1',
        color: '#e53935',
        config: this.selectedTempConfigEye1,
      },
      {
        data: this.temperatureEye2Data,
        chart: 'temperatureEye2Chart',
        ref: 'temperatureEye2ChartRef',
        label: 'EYE #2',
        color: '#d81b60',
        config: this.selectedTempConfigEye2,
      },
      {
        data: this.temperatureEye3Data,
        chart: 'temperatureEye3Chart',
        ref: 'temperatureEye3ChartRef',
        label: 'EYE #3',
        color: '#8e24aa',
        config: this.selectedTempConfigEye3,
      },
      {
        data: this.temperatureEye4Data,
        chart: 'temperatureEye4Chart',
        ref: 'temperatureEye4ChartRef',
        label: 'EYE #4',
        color: '#5e35b1',
        config: this.selectedTempConfigEye4,
      },
    ];

    charts.forEach((chartInfo) => {
      if (
        chartInfo.data.length > 0 &&
        (this as any)[chartInfo.ref] &&
        (this as any)[chartInfo.ref].nativeElement
      ) {
        try {
          const minLimit =
            chartInfo.config?.temperature_min ?? this.DEFAULT_TEMP_LIMITS.min;
          const maxLimit =
            chartInfo.config?.temperature_max ?? this.DEFAULT_TEMP_LIMITS.max;

          const config = this.createTemperatureChartConfig(
            chartInfo.data,
            chartInfo.label,
            chartInfo.color,
            minLimit,
            maxLimit
          );
          (this as any)[chartInfo.chart] = new Chart(
            (this as any)[chartInfo.ref].nativeElement,
            config
          );
          console.log(
            `${chartInfo.label} chart created successfully with limits: ${minLimit}°C - ${maxLimit}°C`
          );
        } catch (error) {
          console.error(`Error creating ${chartInfo.label} chart:`, error);
        }
      }
    });
  }

  // Método para verificar si hay datos en los sensores BLE
  hasTemperatureBleData(): boolean {
    return (
      this.temperatureBle1Data.length > 0 ||
      this.temperatureBle2Data.length > 0 ||
      this.temperatureBle3Data.length > 0 ||
      this.temperatureBle4Data.length > 0
    );
  }

  // Método para verificar si hay datos en los sensores EYE
  hasTemperatureEyeData(): boolean {
    return (
      this.temperatureEye1Data.length > 0 ||
      this.temperatureEye2Data.length > 0 ||
      this.temperatureEye3Data.length > 0 ||
      this.temperatureEye4Data.length > 0
    );
  }

  // Método para verificar si hay datos en los sensores BLE de humedad
  hasHumidityBleData(): boolean {
    return (
      this.humidityBle1Data.length > 0 ||
      this.humidityBle2Data.length > 0 ||
      this.humidityBle3Data.length > 0 ||
      this.humidityBle4Data.length > 0
    );
  }

  // Método para verificar si hay datos en los sensores EYE de humedad
  hasHumidityEyeData(): boolean {
    return (
      this.humidityEye1Data.length > 0 ||
      this.humidityEye2Data.length > 0 ||
      this.humidityEye3Data.length > 0 ||
      this.humidityEye4Data.length > 0
    );
  }

  private createHumidityBleCharts(): void {
    const charts = [
      {
        data: this.humidityBle1Data,
        chart: 'humidityBle1Chart',
        ref: 'humidityBle1ChartRef',
        label: 'BLE #1',
        color: '#00acc1',
        config: this.selectedHumConfigBle1,
      },
      {
        data: this.humidityBle2Data,
        chart: 'humidityBle2Chart',
        ref: 'humidityBle2ChartRef',
        label: 'BLE #2',
        color: '#26a69a',
        config: this.selectedHumConfigBle2,
      },
      {
        data: this.humidityBle3Data,
        chart: 'humidityBle3Chart',
        ref: 'humidityBle3ChartRef',
        label: 'BLE #3',
        color: '#42a5f5',
        config: this.selectedHumConfigBle3,
      },
      {
        data: this.humidityBle4Data,
        chart: 'humidityBle4Chart',
        ref: 'humidityBle4ChartRef',
        label: 'BLE #4',
        color: '#5c6bc0',
        config: this.selectedHumConfigBle4,
      },
    ];

    charts.forEach((chartInfo) => {
      if (
        chartInfo.data.length > 0 &&
        (this as any)[chartInfo.ref] &&
        (this as any)[chartInfo.ref].nativeElement
      ) {
        try {
          const minLimit =
            chartInfo.config?.humidity_min ?? this.DEFAULT_HUMIDITY_LIMITS.min;
          const maxLimit =
            chartInfo.config?.humidity_max ?? this.DEFAULT_HUMIDITY_LIMITS.max;

          const config = this.createHumidityChartConfig(
            chartInfo.data,
            chartInfo.label,
            chartInfo.color,
            minLimit,
            maxLimit
          );
          (this as any)[chartInfo.chart] = new Chart(
            (this as any)[chartInfo.ref].nativeElement,
            config
          );
          console.log(
            `${chartInfo.label} humidity chart created successfully with limits: ${minLimit}% - ${maxLimit}%`
          );
        } catch (error) {
          console.error(
            `Error creating ${chartInfo.label} humidity chart:`,
            error
          );
        }
      }
    });
  }

  private createHumidityEyeCharts(): void {
    const charts = [
      {
        data: this.humidityEye1Data,
        chart: 'humidityEye1Chart',
        ref: 'humidityEye1ChartRef',
        label: 'EYE #1',
        color: '#e53935',
        config: this.selectedHumConfigEye1,
      },
      {
        data: this.humidityEye2Data,
        chart: 'humidityEye2Chart',
        ref: 'humidityEye2ChartRef',
        label: 'EYE #2',
        color: '#d81b60',
        config: this.selectedHumConfigEye2,
      },
      {
        data: this.humidityEye3Data,
        chart: 'humidityEye3Chart',
        ref: 'humidityEye3ChartRef',
        label: 'EYE #3',
        color: '#8e24aa',
        config: this.selectedHumConfigEye3,
      },
      {
        data: this.humidityEye4Data,
        chart: 'humidityEye4Chart',
        ref: 'humidityEye4ChartRef',
        label: 'EYE #4',
        color: '#5e35b1',
        config: this.selectedHumConfigEye4,
      },
    ];

    charts.forEach((chartInfo) => {
      if (
        chartInfo.data.length > 0 &&
        (this as any)[chartInfo.ref] &&
        (this as any)[chartInfo.ref].nativeElement
      ) {
        try {
          const minLimit =
            chartInfo.config?.humidity_min ?? this.DEFAULT_HUMIDITY_LIMITS.min;
          const maxLimit =
            chartInfo.config?.humidity_max ?? this.DEFAULT_HUMIDITY_LIMITS.max;

          const config = this.createHumidityChartConfig(
            chartInfo.data,
            chartInfo.label,
            chartInfo.color,
            minLimit,
            maxLimit
          );
          (this as any)[chartInfo.chart] = new Chart(
            (this as any)[chartInfo.ref].nativeElement,
            config
          );
          console.log(
            `${chartInfo.label} humidity chart created successfully with limits: ${minLimit}% - ${maxLimit}%`
          );
        } catch (error) {
          console.error(
            `Error creating ${chartInfo.label} humidity chart:`,
            error
          );
        }
      }
    });
  }

  private extractTemperatureBleData(): void {
    // Reiniciar los arrays
    this.temperatureBle1Data = [];
    this.temperatureBle2Data = [];
    this.temperatureBle3Data = [];
    this.temperatureBle4Data = [];

    if (!this.storeHistory?.detailsHistory?.frames) {
      return;
    }

    this.storeHistory.detailsHistory?.frames.forEach((frame) => {
      if (frame.IOs) {
        // Procesar cada sensor BLE individualmente
        for (let i = 1; i <= 4; i++) {
          const key = `BLE Temperature #${i}`;
          const value = frame.IOs?.[key];

          if (typeof value === 'number' && value >= -40 && value <= 125) {
            const dataPoint = {
              timestamp: new Date(frame.date),
              temperature: parseFloat(value.toFixed(2)),
            };

            switch (i) {
              case 1:
                this.temperatureBle1Data.push(dataPoint);
                break;
              case 2:
                this.temperatureBle2Data.push(dataPoint);
                break;
              case 3:
                this.temperatureBle3Data.push(dataPoint);
                break;
              case 4:
                this.temperatureBle4Data.push(dataPoint);
                break;
            }
          }
        }
      }
    });
  }

  private extractTemperatureEyeData(): void {
    // Reiniciar los arrays
    this.temperatureEye1Data = [];
    this.temperatureEye2Data = [];
    this.temperatureEye3Data = [];
    this.temperatureEye4Data = [];

    if (!this.storeHistory?.detailsHistory?.frames) {
      return;
    }

    this.storeHistory.detailsHistory?.frames.forEach((frame) => {
      if (frame.IOs) {
        // Procesar cada sensor EYE individualmente
        for (let i = 1; i <= 4; i++) {
          const key = `EYE Temperature ${i}`;
          const value = frame.IOs?.[key];

          if (typeof value === 'number' && value >= -40 && value <= 125) {
            const dataPoint = {
              timestamp: new Date(frame.date),
              temperature: parseFloat(value.toFixed(2)),
            };

            switch (i) {
              case 1:
                this.temperatureEye1Data.push(dataPoint);
                break;
              case 2:
                this.temperatureEye2Data.push(dataPoint);
                break;
              case 3:
                this.temperatureEye3Data.push(dataPoint);
                break;
              case 4:
                this.temperatureEye4Data.push(dataPoint);
                break;
            }
          }
        }
      }
    });
  }

  private extractHumidityBleData(): void {
    // Reiniciar los arrays
    this.humidityBle1Data = [];
    this.humidityBle2Data = [];
    this.humidityBle3Data = [];
    this.humidityBle4Data = [];

    if (!this.storeHistory?.detailsHistory?.frames) {
      return;
    }

    this.storeHistory.detailsHistory?.frames.forEach((frame) => {
      if (frame.IOs) {
        // Procesar cada sensor BLE individualmente
        for (let i = 1; i <= 4; i++) {
          const key = `BLE Humidity #${i}`;
          const value = frame.IOs?.[key];

          if (typeof value === 'number' && value >= 0 && value <= 100) {
            const dataPoint = {
              timestamp: new Date(frame.date),
              humidity: parseFloat(value.toFixed(2)),
            };

            switch (i) {
              case 1:
                this.humidityBle1Data.push(dataPoint);
                break;
              case 2:
                this.humidityBle2Data.push(dataPoint);
                break;
              case 3:
                this.humidityBle3Data.push(dataPoint);
                break;
              case 4:
                this.humidityBle4Data.push(dataPoint);
                break;
            }
          }
        }
      }
    });
  }

  private extractHumidityEyeData(): void {
    // Reiniciar los arrays
    this.humidityEye1Data = [];
    this.humidityEye2Data = [];
    this.humidityEye3Data = [];
    this.humidityEye4Data = [];

    if (!this.storeHistory?.detailsHistory?.frames) {
      return;
    }

    this.storeHistory.detailsHistory?.frames.forEach((frame) => {
      if (frame.IOs) {
        // Procesar cada sensor EYE individualmente
        for (let i = 1; i <= 4; i++) {
          const key = `EYE Humidity ${i}`;
          const value = frame.IOs?.[key];

          if (typeof value === 'number' && value >= 0 && value <= 100) {
            const dataPoint = {
              timestamp: new Date(frame.date),
              humidity: parseFloat(value.toFixed(2)),
            };

            switch (i) {
              case 1:
                this.humidityEye1Data.push(dataPoint);
                break;
              case 2:
                this.humidityEye2Data.push(dataPoint);
                break;
              case 3:
                this.humidityEye3Data.push(dataPoint);
                break;
              case 4:
                this.humidityEye4Data.push(dataPoint);
                break;
            }
          }
        }
      }
    });
  }

  private createTemperatureChartConfig(
    temperatureData: { timestamp: Date; temperature: number }[],
    label: string,
    color: string,
    minLimit: number = -10,
    maxLimit: number = 50
  ): any {
    const labels = temperatureData.map((item) =>
      item.timestamp.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })
    );

    // Crear arrays de colores dinámicos basados en los límites
    // Paleta Binance/TradingView - colores más vibrantes
    const pointColors = temperatureData.map((item) => {
      if (item.temperature < minLimit || item.temperature > maxLimit) {
        return '#ff3b30'; // Rojo más brillante para fuera de límites
      } else {
        return '#30d158'; // Verde más brillante para dentro de límites
      }
    });

    const pointBorderColors = temperatureData.map((item) => {
      if (item.temperature < minLimit || item.temperature > maxLimit) {
        return '#d70015'; // Rojo más oscuro
      } else {
        return '#248a3d'; // Verde más oscuro
      }
    });

    return {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: `Temperatura ${label} (°C)`,
            data: temperatureData.map((item) => item.temperature),
            borderColor: 'transparent', // Línea principal blanca
            backgroundColor: '#c2185b',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: pointColors,
            pointBorderColor: pointBorderColors,
            pointBorderWidth: 2,
          },
          // Línea de límite máximo (naranja Binance, sin label)
          {
            label: `Máx ${maxLimit}°C`,
            data: new Array(temperatureData.length).fill(maxLimit),
            borderColor: '#ff9500', // Naranja Binance
            borderWidth: 2,
            borderDash: [], // Línea sólida
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
          },
          // Línea de límite mínimo (naranja Binance, sin label)
          {
            label: `Min ${minLimit}°C`,
            data: new Array(temperatureData.length).fill(minLimit),
            borderColor: '#ff9500', // Naranja Binance
            borderWidth: 2,
            borderDash: [], // Línea sólida
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              font: {
                size: 12,
                weight: '500',
              },
              // Oculta los datasets sin label
              filter: (legendItem: any) => legendItem.text !== '',
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#ddd',
            borderWidth: 1,
            callbacks: {
              label: (context: any) => {
                // Hover para la línea principal
                if (context.datasetIndex === 0) {
                  const value = context.parsed.y;
                  const status =
                    value < minLimit || value > maxLimit
                      ? '⚠️ Fuera de límites'
                      : '✅ Dentro de límites';
                  return [`${context.dataset.label}: ${value}°C`, status];
                }
                // Hover para las líneas de límite
                if (context.datasetIndex === 1) {
                  return [`Máximo: ${maxLimit}°C`];
                }
                if (context.datasetIndex === 2) {
                  return [`Mínimo: ${minLimit}°C`];
                }
                return '';
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Tiempo',
              font: {
                size: 14,
                weight: '600',
              },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false,
            },
            ticks: {
              maxTicksLimit: 8,
              font: {
                size: 11,
              },
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Temperatura (°C)',
              font: {
                size: 14,
                weight: '600',
              },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false,
            },
            ticks: {
              font: {
                size: 11,
              },
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    };
  }

  private createTemperatureBleChartConfig(
    temperatureData: { timestamp: Date; temperature: number }[]
  ): any {
    return this.createTemperatureChartConfig(temperatureData, 'BLE', '#00acc1');
  }

  private createTemperatureEyeChartConfig(
    temperatureData: { timestamp: Date; temperature: number }[]
  ): any {
    return this.createTemperatureChartConfig(temperatureData, 'EYE', '#e53935');
  }

  private createHumidityChartConfig(
    humidityData: { timestamp: Date; humidity: number }[],
    label: string,
    color: string,
    minLimit: number = 30,
    maxLimit: number = 80
  ): any {
    const labels = humidityData.map((item) =>
      item.timestamp.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })
    );

    // Crear arrays de colores dinámicos basados en los límites
    // Paleta Binance/TradingView - colores más vibrantes
    const pointColors = humidityData.map((item) => {
      if (item.humidity < minLimit || item.humidity > maxLimit) {
        return '#ff3b30'; // Rojo más brillante para fuera de límites
      } else {
        return '#30d158'; // Verde más brillante para dentro de límites
      }
    });

    const pointBorderColors = humidityData.map((item) => {
      if (item.humidity < minLimit || item.humidity > maxLimit) {
        return '#d70015'; // Rojo más oscuro
      } else {
        return '#248a3d'; // Verde más oscuro
      }
    });

    return {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: `Humedad ${label} (%)`,
            data: humidityData.map((item) => item.humidity),
            borderColor: 'transparent', // Línea principal blanca
            backgroundColor: '#0097a7', // Gradiente rojo hacia abajo
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: pointColors,
            pointBorderColor: pointBorderColors,
            pointBorderWidth: 2,
          },
          // Línea de límite máximo (naranja Binance, sin label)
          {
            label: `Máx ${maxLimit}%`,
            data: new Array(humidityData.length).fill(maxLimit),
            borderColor: '#ff9500', // Naranja Binance
            borderWidth: 2,
            borderDash: [], // Línea sólida
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
          },
          // Línea de límite mínimo (naranja Binance, sin label)
          {
            label: `Min ${minLimit}%`,
            data: new Array(humidityData.length).fill(minLimit),
            borderColor: '#ff9500', // Naranja Binance
            borderWidth: 2,
            borderDash: [], // Línea sólida
            fill: false,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              font: {
                size: 12,
                weight: '500',
              },
              // Oculta los datasets sin label
              filter: (legendItem: any) => legendItem.text !== '',
            },
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#ddd',
            borderWidth: 1,
            callbacks: {
              label: (context: any) => {
                // Hover para la línea principal
                if (context.datasetIndex === 0) {
                  const value = context.parsed.y;
                  const status =
                    value < minLimit || value > maxLimit
                      ? '⚠️ Fuera de límites'
                      : '✅ Dentro de límites';
                  return [`${context.dataset.label}: ${value}%`, status];
                }
                // Hover para las líneas de límite
                if (context.datasetIndex === 1) {
                  return [`Máximo: ${maxLimit}%`];
                }
                if (context.datasetIndex === 2) {
                  return [`Mínimo: ${minLimit}%`];
                }
                return '';
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Tiempo',
              font: {
                size: 14,
                weight: '600',
              },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false,
            },
            ticks: {
              maxTicksLimit: 8,
              font: {
                size: 11,
              },
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Humedad (%)',
              font: {
                size: 14,
                weight: '600',
              },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false,
            },
            min: 0,
            max: 100,
            ticks: {
              font: {
                size: 11,
              },
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
      },
    };
  }

  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  formatDistance(km: number): string {
    if (km < 1) {
      return `${Math.round(km * 1000)} m`;
    }
    return `${km.toFixed(2)} km`;
  }

  formatSpeed(speed: number): string {
    return `${speed.toFixed(1)} km/h`;
  }

  formatAltitude(altitude: number): string {
    return `${Math.round(altitude)} m`;
  }

  // Funciones mock para testing
  private mockDataTemperaturaBLE(): { timestamp: Date; temperature: number }[] {
    const mockData: { timestamp: Date; temperature: number }[] = [];
    const now = new Date();

    // Generar 50 puntos de datos con variaciones realistas
    for (let i = 0; i < 50; i++) {
      const timestamp = new Date(now.getTime() - (50 - i) * 60000); // Cada minuto
      const baseTemp = 25; // Temperatura base de 25°C
      const variation = Math.sin(i * 0.2) * 10 + Math.random() * 5 - 2.5; // Variación sinusoidal + ruido
      const temperature = parseFloat((baseTemp + variation).toFixed(2));

      mockData.push({ timestamp, temperature });
    }

    return mockData;
  }

  private mockDataTemperaturaEYE(): { timestamp: Date; temperature: number }[] {
    const mockData: { timestamp: Date; temperature: number }[] = [];
    const now = new Date();

    // Generar 50 puntos de datos con variaciones diferentes a BLE
    for (let i = 0; i < 50; i++) {
      const timestamp = new Date(now.getTime() - (50 - i) * 60000); // Cada minuto
      const baseTemp = 30; // Temperatura base de 30°C (más alta que BLE)
      const variation = Math.cos(i * 0.15) * 8 + Math.random() * 4 - 2; // Variación cosinusoidal + ruido
      const temperature = parseFloat((baseTemp + variation).toFixed(2));

      mockData.push({ timestamp, temperature });
    }

    return mockData;
  }

  // Propiedad para controlar estado de exportación
  isGeneratingPDF: boolean = false;

  // Método mejorado para exportar PDF con contenido largo
  async generateCompletePDF(): Promise<void> {
    this.isGeneratingPDF = true;

    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;

      // Capturar todo el contenido del dashboard de una vez
      const dashboardElement = document.querySelector(
        '.dashboard-history'
      ) as HTMLElement;

      if (!dashboardElement) {
        throw new Error('No se encontró el elemento dashboard');
      }

      // Configurar elemento para captura completa
      const originalStyles = {
        height: dashboardElement.style.height,
        overflow: dashboardElement.style.overflow,
        maxHeight: dashboardElement.style.maxHeight,
      };

      dashboardElement.style.height = 'auto';
      dashboardElement.style.overflow = 'visible';
      dashboardElement.style.maxHeight = 'none';

      // Esperar un momento para que se apliquen los estilos
      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas.default(dashboardElement, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: dashboardElement.scrollWidth,
        height: dashboardElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      // Restaurar estilos originales
      dashboardElement.style.height = originalStyles.height;
      dashboardElement.style.overflow = originalStyles.overflow;
      dashboardElement.style.maxHeight = originalStyles.maxHeight;

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Calcular cuántas páginas necesitamos
      const maxHeightPerPage = pageHeight - margin * 2;
      const totalPages = Math.ceil(imgHeight / maxHeightPerPage);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }

        const sourceY = (page * maxHeightPerPage * canvas.width) / contentWidth;
        const sourceHeight = Math.min(
          (maxHeightPerPage * canvas.width) / contentWidth,
          canvas.height - sourceY
        );

        // Crear canvas para esta página
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;

        const pageCtx = pageCanvas.getContext('2d')!;
        pageCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sourceHeight,
          0,
          0,
          canvas.width,
          sourceHeight
        );

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const pageImgHeight = Math.min(
          maxHeightPerPage,
          imgHeight - page * maxHeightPerPage
        );

        pdf.addImage(
          pageImgData,
          'JPEG',
          margin,
          margin,
          imgWidth,
          pageImgHeight
        );

        // Agregar número de página
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Página ${page + 1} de ${totalPages}`,
          pageWidth - margin - 30,
          pageHeight - 10
        );
      }

      // Generar nombre del archivo
      const vehicleName = this.dashboardData?.vehicleInfo?.imei || 'Vehiculo';
      const date = new Date().toISOString().slice(0, 10);
      const fileName = `Dashboard_${vehicleName}_${date}.pdf`;

      pdf.save(fileName);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF. Por favor, inténtalo de nuevo.');
    } finally {
      this.isGeneratingPDF = false;
      this.cdr.detectChanges();
    }
  }

  // Método alternativo: Exportar en ventana nueva optimizada para impresión
  async exportInNewWindow(): Promise<void> {
    try {
      // Crear ventana nueva con dimensiones optimizadas
      const newWindow = window.open(
        '',
        '_blank',
        'width=1400,height=900,scrollbars=yes'
      );

      if (!newWindow) {
        alert(
          'Por favor, permite las ventanas emergentes para exportar el dashboard'
        );
        return;
      }

      // Obtener el HTML completo del dashboard
      const dashboardElement = document.querySelector('.dashboard-history');

      if (!dashboardElement) {
        console.error('No se encontró el elemento dashboard');
        return;
      }

      // Crear HTML optimizado para la nueva ventana
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Dashboard - ${
            this.dashboardData?.vehicleInfo?.unitName || 'Vehículo'
          }</title>
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #fff;
              color: #333;
              line-height: 1.5;
              padding: 20px;
            }
            .dashboard-history { 
              max-width: 1200px; 
              margin: 0 auto; 
              height: auto !important;
              overflow: visible !important;
            }
            .chart-container { 
              margin-bottom: 30px; 
              page-break-inside: avoid;
              border: 1px solid #eee;
              border-radius: 8px;
              padding: 20px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              height: auto !important;
            }
            .chart-header { 
              margin-bottom: 15px; 
              padding-bottom: 10px;
              border-bottom: 2px solid #f0f0f0;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .chart-header h4 { 
              color: #2c3e50; 
              font-size: 1.2rem; 
              margin-bottom: 8px;
            }
            .chart-info { 
              display: flex; 
              align-items: center; 
              gap: 10px; 
              flex-wrap: wrap;
            }
            .info-badge {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
            }
            .info-badge.temperature { background: #e91e63; color: white; }
            .info-badge.humidity { background: #00bcd4; color: white; }
            .info-badge.speed { background: #2196f3; color: white; }
            .info-badge.elevation { background: #4caf50; color: white; }
            .chart-wrapper { 
              height: 400px !important; 
              background: #fafbfc;
              border-radius: 6px;
              overflow: visible !important;
            }
            .dashboard-header {
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #e0e0e0;
            }
            .vehicle-info h3 {
              color: #1976d2;
              font-size: 1.8rem;
              margin-bottom: 10px;
            }
            .vehicle-info p {
              margin-bottom: 5px;
              color: #666;
            }
            .config-selector { display: none; } /* Ocultar selectores en impresión */
            .dashboard-controls { display: none; } /* Ocultar controles en impresión */
            
            @media print {
              body { 
                margin: 0; 
                padding: 15px;
                font-size: 12px;
              }
              .chart-container { 
                page-break-inside: avoid; 
                margin-bottom: 15px;
                box-shadow: none;
                border: 1px solid #ccc;
              }
              .chart-wrapper {
                height: 350px !important;
              }
              .print-button { display: none !important; }
            }
            
            .print-button {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
              padding: 12px 24px;
              background: linear-gradient(135deg, #1976d2, #1565c0);
              color: white;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              font-weight: bold;
              font-size: 14px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              transition: all 0.3s ease;
            }
            .print-button:hover {
              background: linear-gradient(135deg, #1565c0, #0d47a1);
              transform: translateY(-2px);
              box-shadow: 0 6px 12px rgba(0,0,0,0.3);
            }
          </style>
        </head>
        <body>
          <button class="print-button" onclick="window.print()">🖨️ Imprimir/Exportar PDF</button>
          ${dashboardElement.outerHTML}
          <script>
            // Auto-focus para mejor UX
            window.focus();
            
            // Optimizar gráficos después de cargar
            setTimeout(() => {
              // Forzar redimensión de gráficos si existen
              if (typeof Chart !== 'undefined') {
                Chart.helpers.each(Chart.instances, function(instance) {
                  instance.resize();
                });
              }
            }, 1000);
          </script>
        </body>
        </html>
      `;

      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (error) {
      console.error('Error abriendo ventana nueva:', error);
      alert('Error al abrir la ventana de exportación.');
    }
  }

  /**
   * Devuelve el color correspondiente a cada rango de velocidad
   * según los estándares de colores definidos en la aplicación
   */
  getSpeedRangeColor(speedRange: string): { 'background-color': string } {
    // Mapa de colores estandarizado según la imagen de referencia
    const colorMap: { [key: string]: string } = {
      '0-10 km/h': '#1b7746', // Verde oscuro
      '10-30 km/h': '#7cb342', // Verde claro
      '30-50 km/h': '#ffca28', // Amarillo
      '50-80 km/h': '#ef6c00', // Naranja
      '80-120 km/h': '#d32f2f', // Rojo
      '120+ km/h': '#b71c1c', // Rojo más oscuro
    };

    // Devuelve el color correspondiente o un color por defecto si no se encuentra
    return {
      'background-color': colorMap[speedRange] || '#777777',
    };
  }
}
